import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '../../../services/api-base';
import type {
  ProductService,
  SavedCard,
  SavedCardsResponse,
  CreateCardRequest,
  UpdateCardRequest,
  Promocode,
  OrderRequest,
  BulkOrderRequest,
  CollectPaymentDTO,
  EntityHierarchy,
  OrderPageResponse,
  OrderFilterMetadataResponse,
  QuoteRequest,
  QuoteResponse,
  FreeFloorPlanEligibility,
} from '../../../types/order.types';
import type { CartResponse, CartRequest } from '../cart/types/cart.types';
import type { OrderItem } from '../order-confirmation/utils/orderTransforms';
import type { ExternalUserDetails } from '../../../types/auth.types';

// ---------------------------------------------------------------------------
// Request param types
// ---------------------------------------------------------------------------

interface PaginatedOrderListParams {
  page: number;
  size: number;
  filterQuery: string;
  sortQuery: string;
}

interface DeleteCartItemsParams {
  userId: string;
  lineItemUUIDs: string;
}

// DV-2131 §3: prefer the stable lineItemUUID; assetId + slug remain as a legacy fallback for a line
// persisted before the field existed (the backend prefers lineItemUUID and falls back to the
// composite). All identity params are optional so either form is valid.
export type DeleteAssetOrderParams = {
  userId: string;
  lineItemUUID?: string;
  assetId?: string;
  slug?: string;
};

interface SetDefaultCardParams {
  customerProfileId: string;
  customerPaymentProfileId: string;
}

interface DeleteCardParams {
  customerProfileId: string;
  customerPaymentProfileId: string;
}

interface ValidatePromoCodeParams {
  promocode: string;
  entity: string;
}

interface BulkUploadPaymentParams {
  fileId: string;
  promocode?: string | null;
  body: CollectPaymentDTO | Record<string, unknown>;
}

interface CreateCartFromAssetParams {
  cartId?: string | null;
  // The mutation merges `cartId` from the URL arg into the body; the body
  // itself only carries `userId` + `editPhotoOrders` (and optionally
  // `items`).
  body: Omit<CartRequest, 'cartId'>;
}

/** DV-3521: args of getOrderByUUID. */
export interface OrderByUUIDParams {
  orderUUID: string;
  /** Resolve the order's property without an entity claim (internal entry). */
  unscoped?: boolean;
}

/**
 * DV-3521: the part of order-service's `GET /orders/{orderId}` reply the
 * assets workspace reads. The full reply is an OrderRequest-shaped document;
 * only the stage is needed, and it is declared here so a caller cannot start
 * depending on the rest by accident.
 */
export interface OrderStatusRead {
  orderUUID?: string | null;
  /** Raw dotted stage, e.g. `order.delivered`; see OrderStatusEnum. */
  orderStatus?: string | null;
}

export interface RecentOrdersForPropertyParams {
  propertyId: string;
  size: number;
}

// DV-2357: download=false → inline (View in a new tab); true → attachment (Download).
interface DownloadInvoicePdfParams {
  orderNumber: string;
  download: boolean;
}

/**
 * Response from POST /order-service/api/v1/orders/bulkupload/payment.
 * Frontend only consumes `orders`; the rest of the backend
 * `OrderBulkUploadRequest` (fileId, fileName, etc.) is not used here.
 */
interface BulkUploadPaymentResponse {
  orders: unknown[];
}

// ---------------------------------------------------------------------------
// API slice
// ---------------------------------------------------------------------------

export const orderApi = createApi({
  reducerPath: 'orderApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['Cart', 'Cards', 'OrderList', 'PropertyOrders'],
  endpoints: (builder) => ({
    // -----------------------------------------------------------------------
    // Order listing (order-service) — moved from propertyApi in Pass 3 since
    // the endpoint belongs to order-service, not property-service.
    // -----------------------------------------------------------------------

    getOrders: builder.query<OrderPageResponse, PaginatedOrderListParams>({
      query: ({ page, size, filterQuery, sortQuery }) => {
        const params = `page=${page}&size=${size}${filterQuery}${sortQuery ? `&${sortQuery}` : ''}`;
        return `/order-service/api/v1/orders?${params}`;
      },
      providesTags: ['OrderList'],
    }),

    getRecentOrdersForProperty: builder.query<
      OrderPageResponse,
      RecentOrdersForPropertyParams
    >({
      query: ({ propertyId, size }) =>
        `/order-service/api/v1/orders?propertyId=${propertyId}&size=${size}&sortBy=updatedAt&sortDir=desc`,
      providesTags: (_r, _e, { propertyId }) => [
        { type: 'PropertyOrders', id: propertyId },
      ],
    }),

    /**
     * DV-3521: one order's stage, for the assets workspace's upload and
     * public-link gates when it was opened for an order that is not the
     * property's latest. `unscoped` (the internal Global Orders entry, which
     * has no account chosen) makes order-service resolve the property without
     * an entity claim -- its `isLegacy` flag, which is what that flag actually
     * controls (see OrderServiceImpl.PROPERTY_LOOKUP_UNSCOPED).
     */
    getOrderByUUID: builder.query<OrderStatusRead, OrderByUUIDParams>({
      query: ({ orderUUID, unscoped }) =>
        `/order-service/api/v1/orders/${orderUUID}${unscoped ? '?isLegacy=true' : ''}`,
    }),

    getOrderFilterMetadata: builder.query<OrderFilterMetadataResponse, void>({
      query: () => '/order-service/api/v1/orders/filter-metadata',
    }),

    // DV-3697: does order-service give this account's orders the free Basic
    // Floor Plan line (DV-3609)? One entry per account, since one cart can
    // hold a parent account and its sub-accounts; the account is a query
    // parameter for the same reason, never `X-Entity-UUID`.
    getFreeFloorPlanEligibility: builder.query<FreeFloorPlanEligibility, string>({
      query: (entityUUID) =>
        `/order-service/api/v1/orders/free-floor-plan/eligibility?entityUUID=${encodeURIComponent(entityUUID)}`,
      // A 403 means "not an account you can check out for": an answer, which
      // a token refresh could not change.
      extraOptions: { forbiddenIsFinal: true },
    }),

    // DV-2357: fetch an order's invoice PDF (published to S3 by NetSuite) as a blob for the
    // View/Download actions in the order row menu. A 404 (no invoice yet, or the reader isn't wired)
    // surfaces to the caller as an error and is shown as an inline alert — it is NOT session-ending
    // (baseQueryWithAuth only ends the session on 401/403).
    downloadInvoicePdf: builder.mutation<Blob, DownloadInvoicePdfParams>({
      query: ({ orderNumber, download }) => ({
        url: `/order-service/api/v1/orders/${orderNumber}/invoice.pdf?download=${download}`,
        responseHandler: (response) => response.blob(),
      }),
    }),

    // -----------------------------------------------------------------------
    // Cart (cart-service)
    // -----------------------------------------------------------------------

    getCart: builder.query<CartResponse, string>({
      // The cart-service 404s when the user has no cart yet (or after a clear).
      // Map that to an empty cart: a 404 left as an RTK error makes callers
      // retain the last-successful data (so a stale `cartId` keeps getting sent
      // as a PUT against a nonexistent cart → "No cart for user") and leaves
      // stale items on screen. An empty cart with cartId '' routes the next add
      // through POST (create) and reconciles the cart UI to empty. Non-404
      // failures still surface as errors.
      async queryFn(userId, _api, _extraOptions, baseQuery) {
        const result = await baseQuery(`/cart-service/api/v1/carts/${userId}`);
        if (result.error) {
          if (result.error.status === 404) {
            const emptyCart: CartResponse = {
              cartId: '',
              userId,
              totalPrice: 0,
              totalInitialPrice: 0,
              assetOrder: false,
              items: [],
              editPhotoOrders: [],
              groupedCart: {},
            };
            return { data: emptyCart };
          }
          return { error: result.error };
        }
        return { data: result.data as CartResponse };
      },
      providesTags: ['Cart'],
    }),

    createCart: builder.mutation<CartResponse, CartRequest>({
      query: (body) => ({
        url: '/cart-service/api/v1/carts',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Cart'],
    }),

    updateCart: builder.mutation<CartResponse, CartRequest>({
      query: (body) => ({
        url: '/cart-service/api/v1/carts',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Cart'],
    }),

    // The cart-service delete endpoints return a 200 whose body is a bare
    // text/plain string ("Cart for userId X deleted"), not JSON. The default
    // 'json' responseHandler throws a PARSING_ERROR on that body, rejecting the
    // mutation despite the 200 — which rolls back the optimistic clear and
    // leaves the deleted items on screen. These mutations ignore the body
    // (typed `void`), so read it as text to let a 200 resolve cleanly.
    deleteCart: builder.mutation<void, string>({
      query: (userId) => ({
        url: `/cart-service/api/v1/carts/${userId}`,
        method: 'DELETE',
        responseHandler: 'text',
      }),
      invalidatesTags: ['Cart'],
    }),

    deleteCartItems: builder.mutation<void, DeleteCartItemsParams>({
      query: ({ userId, lineItemUUIDs }) => ({
        url: `/cart-service/api/v1/carts/${userId}?lineItemUUIDs=${lineItemUUIDs}`,
        method: 'DELETE',
        responseHandler: 'text',
      }),
      invalidatesTags: ['Cart'],
    }),

    deleteAssetOrder: builder.mutation<void, DeleteAssetOrderParams>({
      query: ({ userId, lineItemUUID, assetId, slug }) => {
        // Send whichever identity params are present. The backend prefers lineItemUUID and falls
        // back to the (assetId, slug) composite for a legacy line that lacks a stable id.
        const params = new URLSearchParams();
        if (lineItemUUID) params.set('lineItemUUID', lineItemUUID);
        if (assetId) params.set('assetId', assetId);
        if (slug) params.set('slug', slug);
        return {
          url: `/cart-service/api/v1/carts/${userId}/asset-orders?${params.toString()}`,
          method: 'DELETE',
          // 200 body is bare text/plain ("...deleted"); read as text so it resolves cleanly
          // instead of throwing a PARSING_ERROR that rolls back the optimistic delete.
          responseHandler: 'text',
        };
      },
      invalidatesTags: ['Cart'],
    }),

    createCartFromAsset: builder.mutation<
      CartResponse,
      CreateCartFromAssetParams
    >({
      query: ({ cartId, body }) => ({
        url: '/cart-service/api/v1/carts',
        method: cartId ? 'PUT' : 'POST',
        body: cartId ? { ...body, cartId } : body,
      }),
      invalidatesTags: ['Cart'],
    }),

    // -----------------------------------------------------------------------
    // Products / Services (product-service)
    // -----------------------------------------------------------------------

    getPrimaryServices: builder.query<ProductService[], void>({
      query: () =>
        '/product-service/api/v1/services?servicesType=primary',
    }),

    getAddOnServices: builder.query<ProductService[], string>({
      query: (primarySlugs) =>
        `/product-service/api/v1/services?servicesType=secondary&primarySlugs=${primarySlugs}`,
    }),

    getAssetServices: builder.query<ProductService[], void>({
      query: () =>
        '/product-service/api/v1/services?servicesType=asset',
    }),

    getServiceQuote: builder.mutation<QuoteResponse, QuoteRequest>({
      query: (body) => ({
        url: '/product-service/api/v1/services/quote',
        method: 'POST',
        body,
      }),
    }),

    // Full catalog (no type filter → repository.findAll). Used by the orders
    // table to show the selected quantity next to user-selectable services.
    // The catalog is effectively static within a session, so cache it for an
    // hour to avoid a refetch on every orders-page mount — pagination,
    // filtering and SSE updates never re-trigger it.
    getAllServices: builder.query<ProductService[], void>({
      query: () => '/product-service/api/v1/services',
      keepUnusedDataFor: 3600,
    }),

    // -----------------------------------------------------------------------
    // Payment / Cards (entity-service)
    // -----------------------------------------------------------------------

    getSavedCards: builder.query<SavedCardsResponse, void>({
      query: () => '/entity-service/api/v1/customer/card',
      providesTags: ['Cards'],
    }),

    createCard: builder.mutation<SavedCard, CreateCardRequest>({
      query: (body) => ({
        url: '/entity-service/api/v1/customer/card',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Cards'],
    }),

    updateCard: builder.mutation<SavedCard, UpdateCardRequest>({
      query: (body) => ({
        url: '/entity-service/api/v1/customer/card',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Cards'],
    }),

    setDefaultCard: builder.mutation<void, SetDefaultCardParams>({
      query: ({ customerProfileId, customerPaymentProfileId }) => ({
        url: `/entity-service/api/v1/customer/card/true?customerProfileId=${customerProfileId}&customerPaymentProfileId=${customerPaymentProfileId}`,
        method: 'PUT',
      }),
      invalidatesTags: ['Cards'],
    }),

    deleteCard: builder.mutation<void, DeleteCardParams>({
      query: ({ customerProfileId, customerPaymentProfileId }) => ({
        url: `/entity-service/api/v1/customer/card?customerProfileId=${customerProfileId}&customerPaymentProfileId=${customerPaymentProfileId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Cards'],
    }),

    // -----------------------------------------------------------------------
    // Promo Codes (order-service)
    // -----------------------------------------------------------------------

    validatePromoCode: builder.query<Promocode[], ValidatePromoCodeParams>({
      query: ({ promocode, entity }) =>
        `/order-service/api/v1/orders/promocodes?promocode=${promocode}&entity=${encodeURIComponent(entity)}`,
    }),

    // -----------------------------------------------------------------------
    // Order Placement (order-service)
    // -----------------------------------------------------------------------

    /**
     * Backend returns the created `Order` JPA entity (40+ fields). The
     * frontend's only structural consumer is the order-confirmation hook,
     * which reads the `OrderItem` subset (services, propertyAccess,
     * propertyId/assetUUID, etc.). Returning the OrderItem subset keeps
     * the wire reads typed without modeling the entire JPA entity.
     */
    placeOrder: builder.mutation<OrderItem, OrderRequest>({
      query: (body) => ({
        url: '/order-service/api/v1/orders',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['OrderList'],
    }),

    placeBulkOrderThroughCart: builder.mutation<unknown, BulkOrderRequest>({
      query: (body) => ({
        url: '/order-service/api/v1/orders/bulk',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Cart', 'OrderList'],
    }),

    placeBulkUploadPayment: builder.mutation<
      BulkUploadPaymentResponse,
      BulkUploadPaymentParams
    >({
      query: ({ fileId, promocode, body }) => {
        const params = promocode
          ? `fileId=${fileId}&promocode=${promocode}`
          : `fileId=${fileId}`;
        return {
          url: `/order-service/api/v1/orders/bulkupload/payment?${params}`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['OrderList'],
    }),

    // -----------------------------------------------------------------------
    // Entity (entity-service)
    // -----------------------------------------------------------------------

    getEntityTree: builder.query<EntityHierarchy, string>({
      query: (entityId) =>
        `/entity-service/api/v1/entities/${entityId}`,
    }),

    // -----------------------------------------------------------------------
    // Identity / User (user-service)
    // -----------------------------------------------------------------------

    /**
     * Loginless token used by the unauthenticated place-order flow.
     * Backend (`POST /api/v1/generate-token`) returns the raw access_token
     * string, not a JSON object.
     */
    generateToken: builder.mutation<string, void>({
      query: () => ({
        url: '/api/v1/generate-token',
        method: 'POST',
        responseHandler: (response) => response.text(),
      }),
    }),

    /**
     * Post-login user fetch — consumed during the Keycloak callback flow and
     * by the entity-resume Loader to determine whether the user has an
     * entity assignment yet.
     */
    getExternalUser: builder.query<ExternalUserDetails, string>({
      query: (userId) => `/api/v1/users/external/${userId}`,
    }),
  }),
});

export const {
  // Order listing
  useGetOrdersQuery,
  useLazyGetOrdersQuery,
  useGetRecentOrdersForPropertyQuery,
  useGetOrderByUUIDQuery,
  useGetOrderFilterMetadataQuery,
  useLazyGetOrderFilterMetadataQuery,
  useGetFreeFloorPlanEligibilityQuery,
  useDownloadInvoicePdfMutation,

  // Cart
  useGetCartQuery,
  useLazyGetCartQuery,
  useCreateCartMutation,
  useUpdateCartMutation,
  useDeleteCartMutation,
  useDeleteCartItemsMutation,
  useDeleteAssetOrderMutation,
  useCreateCartFromAssetMutation,

  // Products / Services — the place-order wizard fires sequential lookups in
  // event handlers, so it takes the lazy primary/add-on variants; the asset
  // listing and the cart's floor plan offer (DV-3204) render eagerly, so they
  // take the non-lazy variants.
  useLazyGetPrimaryServicesQuery,
  useGetPrimaryServicesQuery,
  useLazyGetAddOnServicesQuery,
  useGetAssetServicesQuery,
  useGetServiceQuoteMutation,
  useGetAllServicesQuery,

  // Payment / Cards
  useGetSavedCardsQuery,
  useCreateCardMutation,
  useUpdateCardMutation,
  useSetDefaultCardMutation,
  useDeleteCardMutation,

  // Promo Codes — customer-facing validate path uses the lazy variant
  // (fires once on apply-button click). Admin CRUD lives in promoCodesApi.
  useLazyValidatePromoCodeQuery,

  // Order Placement
  usePlaceOrderMutation,
  usePlaceBulkOrderThroughCartMutation,
  usePlaceBulkUploadPaymentMutation,

  // Entity
  useGetEntityTreeQuery,
  useLazyGetEntityTreeQuery,

  // Identity / User
  useGenerateTokenMutation,
  useLazyGetExternalUserQuery,
} = orderApi;
